import { useEffect } from 'react'
import {
  captureTrackingParams,
  injectParamsIntoIframe,
  clearTrackingParams,
} from '../utils/utmTracking'
import { getQuizDataAsURLParams } from '../utils/quizDataPersistence'

/**
 * React Hook: Capture UTM parameters on page load
 *
 * Use this hook on every page/component to ensure tracking params
 * are captured whenever they appear in the URL.
 *
 * Example usage:
 * ```
 * function MyComponent() {
 *   useUTMCapture();
 *   return <div>...</div>;
 * }
 * ```
 */
export function useUTMCapture(): void {
  useEffect(() => {
    // Capture tracking params on component mount
    captureTrackingParams()
  }, [])
}

/**
 * React Hook: Inject UTM + Quiz Data into iframe
 *
 * Use this hook on the page containing the GHL form iframe.
 * It will automatically inject stored tracking params AND quiz data into the iframe.
 *
 * @param iframeId - The ID of the iframe element
 * @param enabled - Whether to enable injection (default: true)
 *
 * Example usage:
 * ```
 * function FormPage() {
 *   useUTMInjection('inline-2WonMk9TvZ0WMulwjg2R');
 *   return <iframe id="inline-2WonMk9TvZ0WMulwjg2R" ... />;
 * }
 * ```
 */
export function useUTMInjection(
  iframeId: string,
  enabled: boolean = true,
): void {
  useEffect(() => {
    if (!enabled) return

    // Wait a bit for iframe to be added to DOM
    const timer = setTimeout(() => {
      // Get quiz data as URL parameters
      const quizParams = getQuizDataAsURLParams()

      // Inject both tracking params and quiz data into iframe
      injectParamsIntoIframe(iframeId, quizParams)
    }, 500)

    return () => clearTimeout(timer)
  }, [iframeId, enabled])
}

/**
 * React Hook: Clear UTM parameters
 *
 * Use this hook on the thank-you/confirmation page to clear
 * stored tracking params after successful conversion.
 *
 * Example usage:
 * ```
 * function ThankYouPage() {
 *   useUTMClear();
 *   return <div>Thank you!</div>;
 * }
 * ```
 */
export function useUTMClear(): void {
  useEffect(() => {
    clearTrackingParams()
  }, [])
}
